import React, { useEffect, useState } from 'react'
import styles from './OptionEdit.module.scss'
import { IconButton } from 'components/IconButton/IconButton'
import { ReactComponent as ThreeDotsIcon } from 'assets/icons/threeDots.svg'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { TrashIcon } from 'components/DeleteLayerModal/TrashIcon'
import cx from 'classnames'
import { type PropertyValue } from 'api/clients/PropertyClient'
import { colorsDarkToLight } from 'common/PropertyForm/ColorInput'

export type OptionEditProps = {
  propertyValue: PropertyValue
  onUpdated?: (propertyValue: PropertyValue) => void
  onDeleted?: (propertyValue: PropertyValue) => void
}

export const OptionEdit: React.FC<React.PropsWithChildren<OptionEditProps>> = ({
  propertyValue,
  onUpdated,
  onDeleted,
}) => {
  const [updatedName, setUpdatedName] = useState(propertyValue?.value)
  const [updatedColor, setUpdatedColor] = useState(propertyValue?.meta?.color)
  const [currentColorIndex, setCurrentColorIndex] = useState(-1)
  const [allowArrowNavColors, setAllowArrowNavColors] = useState(false)

  useEffect(() => {
    if (currentColorIndex < 0) return

    colorsDarkToLight.forEach((color, index) => {
      if (color === updatedColor) {
        setCurrentColorIndex(index)
      }
    })
  }, [currentColorIndex, setCurrentColorIndex, updatedColor])

  const handleUpdate = () => {
    if (updatedName === propertyValue?.value && updatedColor === propertyValue?.meta?.color) return

    onUpdated?.({
      ...propertyValue,
      value: updatedName,
      meta: {
        ...propertyValue?.meta,
        color: updatedColor,
      },
    } as PropertyValue)
  }

  const updateName = () => {
    if (updatedName === propertyValue?.value) return

    onUpdated?.({
      ...propertyValue,
      value: updatedName,
    } as PropertyValue)
  }

  const updateColor = (color: string) => {
    if (color === updatedColor) return

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    onUpdated!({
      ...propertyValue,
      meta: {
        ...propertyValue?.meta,
        color,
      },
    } as PropertyValue)

    setUpdatedColor(color)
  }

  return (
    <DropdownTrigger onClose={handleUpdate} placement="bottom-start">
      <IconButton className={cx(styles.resultIcon)} icon={ThreeDotsIcon} />
      {(close) => (
        <div
          className={styles.editMenu}
          onClick={(e) => {
            e.stopPropagation()
          }}
          onKeyDown={(e) => {
            let newIndex = currentColorIndex
            e.stopPropagation()

            if (e.key === 'Enter') {
              handleUpdate()
              close()
              return
            }

            if (!allowArrowNavColors) return

            switch (e.key) {
              case 'ArrowUp':
                newIndex = Math.max(currentColorIndex - 10, 0)
                break
              case 'ArrowDown':
                newIndex = Math.min(currentColorIndex + 10, 39)
                break
              case 'ArrowRight':
                newIndex = Math.min(currentColorIndex + 1, 39)
                break
              case 'Tab':
                if (e.shiftKey) {
                  newIndex = Math.max(currentColorIndex - 1, 0)
                } else {
                  newIndex = Math.min(currentColorIndex + 1, 39)
                }
                break
              case 'ArrowLeft':
                newIndex = Math.max(currentColorIndex - 1, 0)
                break
              default:
                break
            }

            setCurrentColorIndex(newIndex)
            setUpdatedColor(colorsDarkToLight[newIndex])
            updateColor(colorsDarkToLight[newIndex])
          }}
        >
          <input
            className={styles.editNameInput}
            value={updatedName}
            onChange={(e) => {
              setUpdatedName(e.target.value)
            }}
            onBlur={() => {
              updateName()
              setAllowArrowNavColors(true)
            }}
            onFocus={() => setAllowArrowNavColors(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation()

                updateName()
                setAllowArrowNavColors(true)
              }
            }}
          />
          <button
            onClick={() => {
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              onDeleted!(propertyValue)
              close()
            }}
            className={styles.deleteButton}
          >
            <TrashIcon />
            Delete
          </button>
          <div className={styles.menuDivider} />
          <div className={styles.colorPicker}>
            {colorsDarkToLight.map((color, index) => {
              return (
                <div key={color} className={cx(styles.colorBorder, { [styles.selected]: updatedColor === color })}>
                  <button
                    onClick={() => {
                      updateColor(color)
                      setCurrentColorIndex(index)
                    }}
                    className={styles.colorButton}
                    style={{ background: color }}
                    onFocus={() => setCurrentColorIndex(index)}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}
    </DropdownTrigger>
  )
}
